import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

interface MobileNavigationState {
  activeTab: string;
  moreMenuOpen: boolean;
  searchOpen: boolean;
  refreshing: boolean;
}

export function useMobileNavigation() {
  const location = useLocation();
  const [state, setState] = useState<MobileNavigationState>({
    activeTab: 'dashboard',
    moreMenuOpen: false,
    searchOpen: false,
    refreshing: false,
  });

  // Update active tab based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path === '/') {
      setState(prev => ({ ...prev, activeTab: 'dashboard' }));
    } else if (path.startsWith('/network')) {
      setState(prev => ({ ...prev, activeTab: 'network' }));
    } else if (path.startsWith('/security')) {
      setState(prev => ({ ...prev, activeTab: 'security' }));
    } else {
      setState(prev => ({ ...prev, activeTab: 'more' }));
    }
  }, [location]);

  // Close menus on route change
  useEffect(() => {
    setState(prev => ({
      ...prev,
      moreMenuOpen: false,
      searchOpen: false,
    }));
  }, [location]);

  const openMoreMenu = useCallback(() => {
    setState(prev => ({ ...prev, moreMenuOpen: true }));
    // Prevent body scroll when menu is open
    document.body.style.overflow = 'hidden';
  }, []);

  const closeMoreMenu = useCallback(() => {
    setState(prev => ({ ...prev, moreMenuOpen: false }));
    // Restore body scroll
    document.body.style.overflow = '';
  }, []);

  const openSearch = useCallback(() => {
    setState(prev => ({ ...prev, searchOpen: true }));
    document.body.style.overflow = 'hidden';
  }, []);

  const closeSearch = useCallback(() => {
    setState(prev => ({ ...prev, searchOpen: false }));
    document.body.style.overflow = '';
  }, []);

  const startRefresh = useCallback(() => {
    setState(prev => ({ ...prev, refreshing: true }));
  }, []);

  const endRefresh = useCallback(() => {
    setState(prev => ({ ...prev, refreshing: false }));
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return {
    ...state,
    openMoreMenu,
    closeMoreMenu,
    openSearch,
    closeSearch,
    startRefresh,
    endRefresh,
  };
}